import React from 'react';
import { Button, Table } from '@odin-labs/components';
import { ChangeType } from 'apollo/generated/client-operations';
import { useModalChangeActions } from 'utils/useModalChangeActions';
import { PlusIcon } from 'components/icons';
import { NewHeader } from 'components/header/NewHeader';
import {
  AddJobsiteFormModal,
  DeleteJobsiteFormModal,
  EditableJobsiteForm,
  EditJobsiteFormModal,
} from './modals';
import { JobsiteFormsProps } from './types';
import { getColumns, JobsiteFormColumn } from './JobsiteForms.tables';

const classes = {
  error: 'odin-mt-2 odin-text-sm odin-text-odin-danger',
};

export const JobsiteForms = React.forwardRef(
  (
    props: JobsiteFormsProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ): React.ReactElement => {
    const { value, onChange, loading, error, formsOptions = [] } = props;

    const {
      change,
      closeModal,
      openAddModal,
      openEditModal,
      openDeleteModal,
      updateValueAndCloseModal,
    } = useModalChangeActions<EditableJobsiteForm>({
      items: value,
      onChange,
      idField: 'id',
    });

    const columns = React.useMemo<JobsiteFormColumn[]>(
      () => getColumns({ openEditModal, openDeleteModal }),
      [openEditModal, openDeleteModal],
    );

    const items = React.useMemo(() => {
      return value?.filter((item) => item.changeType !== ChangeType.Removed);
    }, [value]);

    const renderError = (): React.ReactNode => {
      if (!error) return null;
      if (typeof error === 'string') {
        return <p className={classes.error}>{error}</p>;
      }
      return <div className={classes.error}>{error}</div>;
    };

    const innerRef = React.useRef<HTMLDivElement>();

    React.useImperativeHandle(ref, () =>
      Object.assign(innerRef.current, {
        focus: () => {
          innerRef.current?.scrollIntoView({ behavior: 'smooth' });
        },
      }),
    );

    return (
      <>
        <div ref={innerRef} className="odin-flex odin-flex-col odin-gap-y-6">
          <NewHeader
            title="Custom Logs"
            size="md"
            actionsProps={{
              headerActions: null,
              children: (
                <Button
                  icon={PlusIcon}
                  text="Add Log"
                  size="sm"
                  onClick={openAddModal}
                />
              ),
            }}
          />
          <Table
            loading={loading}
            columns={columns}
            data={items}
            onRowClick={({ data: jobsiteForm }) => openEditModal(jobsiteForm)}
            remote
            disableGlobalFilter
            disableSortBy
          />
        </div>
        {renderError()}
        <AddJobsiteFormModal
          isOpen={change?.type === 'create'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          jobsiteForms={value}
          formsOptions={formsOptions}
        />
        <EditJobsiteFormModal
          isOpen={change?.type === 'update'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          jobsiteForms={value}
          jobsiteForm={change?.item}
          formsOptions={formsOptions}
        />
        <DeleteJobsiteFormModal
          isOpen={change?.type === 'remove'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          jobsiteForm={change?.item}
        />
      </>
    );
  },
);
